import React, { useState, useEffect } from 'react';
import { ExternalLink, Play, Globe, Video, Code, FileText, Palette, GitBranch, Figma } from 'lucide-react';
import { 
    getYouTubeThumbnail, 
    getDomainFromUrl, 
    isSafeUrl, 
    extractYouTubeVideoId,
    getPlatformType 
} from '../utils/linkUtils';

const LinkPreview = ({ url, isYouTube, isVideo, isDesign, isDev, isDocument, platformType, videoId, className = '' }) => {
    const [thumbnailError, setThumbnailError] = useState(false);
    const [showPreview, setShowPreview] = useState(true);

    // Reset thumbnail error when URL changes
    useEffect(() => {
        setThumbnailError(false);
    }, [url]);

    // Don't render if URL is not safe
    if (!isSafeUrl(url)) {
        return (
            <span className="text-gray-500 italic">
                [Unsafe URL removed]
            </span>
        );
    }

    const domain = getDomainFromUrl(url);
    const actualPlatformType = platformType || getPlatformType(url);

    // Handle YouTube videos with thumbnail preview
    if (isYouTube && videoId && showPreview && !thumbnailError) {
        return (
            <div className={`link-preview youtube-preview ${className}`}>
                <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden max-w-sm hover:shadow-md transition-shadow duration-200">
                    {/* Thumbnail */}
                    <div className="relative">
                        <img
                            src={getYouTubeThumbnail(videoId, 'medium')}
                            alt="YouTube video thumbnail"
                            className="w-full h-32 object-cover"
                            onError={() => setThumbnailError(true)}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-red-600 text-white rounded-full p-2 shadow-lg hover:bg-red-700 transition-colors">
                                <Play className="h-6 w-6 fill-current" />
                            </div>
                        </div>

                    </div>
                    
                    {/* Content */}
                    <div className="p-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Video className="h-4 w-4" />
                                <span>Video</span>
                            </div>
                            <button
                                onClick={() => setShowPreview(false)}
                                className="text-gray-400 hover:text-gray-600 text-xs"
                                title="Hide preview"
                            >
                                ×
                            </button>
                        </div>
                        
                        {/* Link */}
                        <div className="mt-2">
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm break-all flex items-center gap-1 hover:underline"
                            >
                                <span className="truncate">{domain}</span>
                                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Handle design tool URLs (Figma, Sketch, etc.)
    if (isDesign && showPreview) {
        const getPlatformInfo = (domain) => {
            if (domain.includes('figma')) return { name: 'Figma', color: 'bg-purple-500', icon: <Palette className="h-6 w-6 text-white" /> };
            if (domain.includes('sketch')) return { name: 'Sketch', color: 'bg-yellow-500', icon: <Palette className="h-6 w-6 text-white" /> };
            if (domain.includes('adobe')) return { name: 'Adobe', color: 'bg-red-500', icon: <Palette className="h-6 w-6 text-white" /> };
            if (domain.includes('canva')) return { name: 'Canva', color: 'bg-blue-500', icon: <Palette className="h-6 w-6 text-white" /> };
            if (domain.includes('miro')) return { name: 'Miro', color: 'bg-yellow-400', icon: <Palette className="h-6 w-6 text-white" /> };
            return { name: 'Design Tool', color: 'bg-purple-500', icon: <Palette className="h-6 w-6 text-white" /> };
        };

        const platformInfo = getPlatformInfo(domain);

        return (
            <div className={`link-preview design-preview ${className}`}>
                <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden max-w-sm hover:shadow-md transition-shadow duration-200">
                    {/* Header with platform branding */}
                    <div className={`${platformInfo.color} p-4 flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                            {platformInfo.icon}
                            <span className="text-white font-semibold">{platformInfo.name}</span>
                        </div>
                        <button
                            onClick={() => setShowPreview(false)}
                            className="text-white hover:text-gray-200 text-lg"
                            title="Hide preview"
                        >
                            ×
                        </button>
                    </div>
                    
                    {/* Content */}
                    <div className="p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <Palette className="h-4 w-4" />
                            <span>Design File</span>
                        </div>
                        
                        {/* Link */}
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm break-all flex items-center gap-1 hover:underline"
                        >
                            <span className="truncate">{domain}</span>
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // Handle development tool URLs (GitHub, CodePen, etc.)
    if (isDev && showPreview) {
        const getPlatformInfo = (domain) => {
            if (domain.includes('github')) return { name: 'GitHub', color: 'bg-gray-900', icon: <GitBranch className="h-6 w-6 text-white" /> };
            if (domain.includes('gitlab')) return { name: 'GitLab', color: 'bg-orange-500', icon: <GitBranch className="h-6 w-6 text-white" /> };
            if (domain.includes('codepen')) return { name: 'CodePen', color: 'bg-black', icon: <Code className="h-6 w-6 text-white" /> };
            if (domain.includes('codesandbox')) return { name: 'CodeSandbox', color: 'bg-blue-500', icon: <Code className="h-6 w-6 text-white" /> };
            if (domain.includes('stackblitz')) return { name: 'StackBlitz', color: 'bg-blue-600', icon: <Code className="h-6 w-6 text-white" /> };
            return { name: 'Code Repository', color: 'bg-gray-700', icon: <Code className="h-6 w-6 text-white" /> };
        };

        const platformInfo = getPlatformInfo(domain);

        return (
            <div className={`link-preview dev-preview ${className}`}>
                <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden max-w-sm hover:shadow-md transition-shadow duration-200">
                    {/* Header with platform branding */}
                    <div className={`${platformInfo.color} p-4 flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                            {platformInfo.icon}
                            <span className="text-white font-semibold">{platformInfo.name}</span>
                        </div>
                        <button
                            onClick={() => setShowPreview(false)}
                            className="text-white hover:text-gray-200 text-lg"
                            title="Hide preview"
                        >
                            ×
                        </button>
                    </div>
                    
                    {/* Content */}
                    <div className="p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <Code className="h-4 w-4" />
                            <span>Code Repository</span>
                        </div>
                        
                        {/* Link */}
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm break-all flex items-center gap-1 hover:underline"
                        >
                            <span className="truncate">{domain}</span>
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // Handle document/collaboration URLs (Google Docs, Notion, etc.)
    if (isDocument && showPreview) {
        const getPlatformInfo = (domain) => {
            if (domain.includes('docs.google') || domain.includes('drive.google')) return { name: 'Google Docs', color: 'bg-blue-500', icon: <FileText className="h-6 w-6 text-white" /> };
            if (domain.includes('notion')) return { name: 'Notion', color: 'bg-gray-800', icon: <FileText className="h-6 w-6 text-white" /> };
            if (domain.includes('airtable')) return { name: 'Airtable', color: 'bg-yellow-500', icon: <FileText className="h-6 w-6 text-white" /> };
            if (domain.includes('trello')) return { name: 'Trello', color: 'bg-blue-600', icon: <FileText className="h-6 w-6 text-white" /> };
            if (domain.includes('dropbox')) return { name: 'Dropbox', color: 'bg-blue-700', icon: <FileText className="h-6 w-6 text-white" /> };
            return { name: 'Document', color: 'bg-green-500', icon: <FileText className="h-6 w-6 text-white" /> };
        };

        const platformInfo = getPlatformInfo(domain);

        return (
            <div className={`link-preview document-preview ${className}`}>
                <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden max-w-sm hover:shadow-md transition-shadow duration-200">
                    {/* Header with platform branding */}
                    <div className={`${platformInfo.color} p-4 flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                            {platformInfo.icon}
                            <span className="text-white font-semibold">{platformInfo.name}</span>
                        </div>
                        <button
                            onClick={() => setShowPreview(false)}
                            className="text-white hover:text-gray-200 text-lg"
                            title="Hide preview"
                        >
                            ×
                        </button>
                    </div>
                    
                    {/* Content */}
                    <div className="p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <FileText className="h-4 w-4" />
                            <span>Document</span>
                        </div>
                        
                        {/* Link */}
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm break-all flex items-center gap-1 hover:underline"
                        >
                            <span className="truncate">{domain}</span>
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // Handle other video URLs
    if (isVideo && showPreview) {
        return (
            <div className={`link-preview video-preview ${className}`}>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-w-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Video className="h-4 w-4" />
                            <span>Video Link</span>
                        </div>
                        <button
                            onClick={() => setShowPreview(false)}
                            className="text-gray-400 hover:text-gray-600 text-xs"
                            title="Hide preview"
                        >
                            ×
                        </button>
                    </div>
                    
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm break-all flex items-center gap-1 hover:underline"
                    >
                        <span className="truncate">{domain}</span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                </div>
            </div>
        );
    }

    // Handle regular URLs (fallback or when preview is disabled)
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline break-all ${className}`}
            title={`Open ${domain} in new tab`}
        >
            <Globe className="h-3 w-3 flex-shrink-0" />
            <span>{domain}</span>
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
        </a>
    );
};

// Component for rendering inline links within text
export const InlineLink = ({ url, isYouTube, isVideo, isDesign, isDev, isDocument, platformType, videoId, className = '' }) => {
    const domain = getDomainFromUrl(url);
    
    if (!isSafeUrl(url)) {
        return <span className="text-gray-500 italic">[Unsafe URL removed]</span>;
    }

    const getIcon = () => {
        if (isYouTube) return <Play className="h-3 w-3 flex-shrink-0 text-red-600" />;
        if (isVideo) return <Video className="h-3 w-3 flex-shrink-0" />;
        if (isDesign) return <Palette className="h-3 w-3 flex-shrink-0 text-purple-600" />;
        if (isDev) return <Code className="h-3 w-3 flex-shrink-0 text-gray-700" />;
        if (isDocument) return <FileText className="h-3 w-3 flex-shrink-0 text-green-600" />;
        return <Globe className="h-3 w-3 flex-shrink-0" />;
    };

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline ${className}`}
            title={`Open ${domain} in new tab`}
        >
            {getIcon()}
            <span>{domain}</span>
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
        </a>
    );
};

// Component for rendering text with parsed links
export const TextWithLinks = ({ text, className = '', showPreviews = true }) => {
    if (!text) return null;

    const { parseTextWithLinks } = require('../utils/linkUtils');
    const parts = parseTextWithLinks(text);

    return (
        <div className={className}>
            {parts.map((part, index) => {
                if (part.type === 'text') {
                    return <span key={index}>{part.content}</span>;
                } else if (part.type === 'link') {
                    // Show full preview for special platforms if showPreviews is true
                    if (showPreviews && (part.isYouTube || part.isDesign || part.isDev || part.isDocument)) {
                        return (
                            <div key={index} className="my-2">
                                <LinkPreview
                                    url={part.url}
                                    isYouTube={part.isYouTube}
                                    isVideo={part.isVideo}
                                    isDesign={part.isDesign}
                                    isDev={part.isDev}
                                    isDocument={part.isDocument}
                                    platformType={part.platformType}
                                    videoId={part.videoId}
                                />
                            </div>
                        );
                    } else {
                        // Show inline link for other URLs or when previews are disabled
                        return (
                            <InlineLink
                                key={index}
                                url={part.url}
                                isYouTube={part.isYouTube}
                                isVideo={part.isVideo}
                                isDesign={part.isDesign}
                                isDev={part.isDev}
                                isDocument={part.isDocument}
                                platformType={part.platformType}
                                videoId={part.videoId}
                            />
                        );
                    }
                }
                return null;
            })}
        </div>
    );
};

export default LinkPreview;
